import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { PiPushPinSimpleFill, PiPushPinSimpleSlashFill, PiTrashFill } from 'react-icons/pi';
import classNames from 'classnames/bind';

import { Note } from '@/store/notesStore';
import { useManageNote } from '@/api/notes/useManageNote';
import { usePinNote } from '@/api/notes/usePinNote';
import { useNotesListContext } from '@/contexts/NotesListContext';
import NoteListTitle from './NoteListTitle';
import NoteTagsDisplay from '@/components/notes/tags/NoteTagsDisplay';
import NoteTagsEditor from '@/components/notes/tags/NoteTagsEditor';
import animStyles from '@/components/notes/NoteAnimations.module.scss';

const cx = classNames.bind(animStyles);

interface NoteListRowProps {
  note: Note;
  searchQuery?: string;
}

const NoteListRow: React.FC<NoteListRowProps> = ({ note, searchQuery }) => {
  const { deleteNoteById } = useManageNote();
  const { togglePin } = usePinNote();
  const { fadingNoteId } = useNotesListContext();
  const [tagsOpen, setTagsOpen] = useState(false);

  const tags = note.tags ?? [];

  const handleRowClick = () => {
    if (note.id) router.visit(`/app/note/${note.id}`);
  };

  const handleTogglePin = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await togglePin(note);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      'Are you sure you want to delete this note? This action cannot be undone.',
    );
    if (confirmed && note.id) {
      await deleteNoteById(note.id);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={tagsOpen ? undefined : handleRowClick}
      onKeyDown={(e) => !tagsOpen && e.key === 'Enter' && handleRowClick()}
      className={cx(
        'flex flex-col px-4 py-3 rounded-lg cursor-pointer',
        'bg-white dark:bg-gray-800',
        'border border-gray-200 dark:border-gray-700',
        'hover:bg-gray-50 dark:hover:bg-gray-700',
        'hover:border-gray-300 dark:hover:border-gray-500',
        'shadow-sm hover:shadow',
        'transition-all group',
        { fadeOut: fadingNoteId === note.id },
      )}
    >
      {/* Main row: title + tags zone + actions */}
      <div className="flex items-center gap-3 min-w-0">
        {/* Title / excerpt */}
        <span className="flex-1 text-sm min-w-0 truncate">
          <NoteListTitle note={note} searchQuery={searchQuery} />
        </span>
        {note.encrypted && <span className="shrink-0 text-xs text-gray-500" title={note.body_locked ? 'Encrypted and locked' : 'Encrypted and unlocked'} aria-label={note.body_locked ? 'Encrypted and locked' : 'Encrypted and unlocked'}>{note.body_locked ? '🔒' : '🔓'}</span>}

        {/* Tags zone — between title and action buttons */}
        <div
          className="shrink-0 max-w-[220px] sm:max-w-xs"
          onClick={(e) => { e.stopPropagation(); setTagsOpen(true); }}
        >
          {tagsOpen ? null : (
            tags.length > 0 ? (
              <NoteTagsDisplay tags={tags} maxVisible={3} />
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500 hover:text-primary-500 dark:hover:text-primary-400 transition-colors whitespace-nowrap">
                Add tag
              </span>
            )
          )}
        </div>

        {/* Actions — visible on hover */}
        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            aria-label={note.pinned ? 'Unpin note' : 'Pin note'}
            onClick={handleTogglePin}
            className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {note.pinned ? (
              <PiPushPinSimpleFill size={16} className="text-yellow-500" />
            ) : (
              <PiPushPinSimpleSlashFill size={16} className="text-gray-400 dark:text-gray-500" />
            )}
          </button>
          <button
            type="button"
            aria-label="Delete note"
            onClick={handleDelete}
            className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
          >
            <PiTrashFill size={16} className="text-gray-400 dark:text-gray-500 hover:text-red-500" />
          </button>
        </div>
      </div>

      {/* Inline tag editor — expands below the main row */}
      {tagsOpen && (
        <div
          className="mt-2"
          onClick={(e) => e.stopPropagation()}
        >
          <NoteTagsEditor
            noteId={note.id!}
            tags={tags}
            onClose={() => setTagsOpen(false)}
          />
        </div>
      )}
    </div>
  );
};

export default NoteListRow;
